import { ColumnType } from 'antd/es/table'
import { defaultSortedColumn } from 'features/fde/helpers/tableHelpers'
import { RegionOfInterest } from 'features/fde/regionTypes'
import { useGeckoLocale } from 'features/locale'
import find from 'lodash/find'
import React from 'react'
import {
  GeckoComponentRect,
  GeckoFormattingVariant,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
} from 'types'

import { extractRulerMeasurement } from './rulerMarkup'

export function getRulerLengthDetailsColumn(
  rulers: ReadonlyArray<RegionOfInterest>,
  cdfGeometry: GeckoComponentRect,
  preferredDisplayUnit: GeckoMeasurementUnit
): ColumnType<RegionOfInterest> {
  return defaultSortedColumn('Length', 'id', (value, region) => {
    const ruler = find(rulers, (ruler) => region.id === ruler.id) ?? null
    const rMeas = extractRulerMeasurement(
      ruler,
      cdfGeometry,
      preferredDisplayUnit
    )

    return rMeas ? (
      <GeckoMeasurementLabel measurement={rMeas} variant='geometric' />
    ) : (
      ''
    )
  })
}

/**
 * A label component which takes in a measurement and formats it in a
 * user-friendly way, taking into account the current measurement system
 * (imperial or metric) and a number of formatting options
 * @param measurement     The measurement or measurement range to format
 * @param variant         The string variant to use (affects string length)
 * @param showLoading     Whether to show a "Loading..." filler string
 */
export function useGeckoMeasurementLabelText(
  measurement: GeckoMeasurement | GeckoMeasurementRange | undefined,
  variant?: GeckoFormattingVariant | 'none',
  showLoading?: boolean
): string | undefined {
  const { formatMeasurement, formatMeasurementRange } = useGeckoLocale()
  if (!measurement) return undefined
  if (showLoading) return 'Loading…'
  if (measurement instanceof GeckoMeasurement) {
    return formatMeasurement({ measurement, variant, showLoading })
  }

  return formatMeasurementRange({ measurement, variant, showLoading })
}

/**
 * A label component which takes in a measurement and formats it in a
 * user-friendly way, taking into account the current measurement system
 * (imperial or metric) and a number of formatting options
 * @param measurement     The measurement or measurement range to format
 * @param variant         The string variant to use (affects string length)
 * @param options         Any additional options used for string formatting
 * @param showLoading     Whether to show a "Loading..." filler string
 * @param className       A class name, used for things like styled formatters
 * @param div             Whether to treat this as a div or a span (default)
 */
export const GeckoMeasurementLabel = React.forwardRef<
  HTMLDivElement,
  {
    measurement: GeckoMeasurement | GeckoMeasurementRange | undefined
    variant?: GeckoFormattingVariant | 'none'
    showLoading?: boolean
    className?: string | undefined
    div?: true // Default value is false
  }
>(({ div, measurement, variant, showLoading, ...rest }, ref) => {
  const str = useGeckoMeasurementLabelText(measurement, variant, showLoading)

  const AsType = div ? 'div' : 'span'
  return !str ? null : (
    <AsType ref={ref} {...rest}>
      {str}
    </AsType>
  )
})

GeckoMeasurementLabel.displayName = 'GeckoMeasurementLabel'
